import { useState, useEffect, useCallback } from 'react';

type UserType = 'client' | 'practitioner';

const BASE_STORAGE_KEYS = {
  client: 'onboarding_completed_client',
  practitioner: 'onboarding_completed_practitioner',
} as const;

export const getOnboardingStorageKey = (userType: UserType, userId?: string) => {
  const baseKey = BASE_STORAGE_KEYS[userType];
  return userId ? `${baseKey}_${userId}` : baseKey;
};

export const useOnboarding = (userType: UserType, userId?: string) => {
  const [isRunning, setIsRunning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const storageKey = getOnboardingStorageKey(userType, userId);

  // Check if onboarding has been completed
  const isCompleted = useCallback(() => {
    return localStorage.getItem(storageKey) === 'true';
  }, [storageKey]);

  // Start the tour
  const startTour = useCallback(() => {
    setStepIndex(0);
    setIsRunning(true);
  }, []);

  // End the tour and mark as completed
  const endTour = useCallback(() => {
    setIsRunning(false);
    localStorage.setItem(storageKey, 'true');
  }, [storageKey]);

  // Reset the tour (for settings page)
  const resetTour = useCallback(() => {
    localStorage.removeItem(storageKey);
    setStepIndex(0);
    setIsRunning(true);
  }, [storageKey]);

  // Auto-start on first visit
  useEffect(() => {
    // Small delay to ensure DOM elements are rendered
    const timer = setTimeout(() => {
      if (!isCompleted()) {
        startTour();
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [isCompleted, startTour]);

  return {
    isRunning,
    setIsRunning,
    stepIndex,
    setStepIndex,
    startTour,
    endTour,
    resetTour,
    isCompleted,
  };
};
